import { Injectable, signal, inject } from '@angular/core';
import { APP_CONFIG, ConfigSettings, SECURITY_API_URL } from '@shared/config';
import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import {
  getIdTokenFromFlutterWebViewEvent,
  isInFlutterWrapper,
} from '@security/flutter-authentication.utils';
import { EMPTY, from, Observable, of, throwError, zip } from 'rxjs';
import {
  catchError,
  finalize,
  ignoreElements,
  map,
  mergeMap,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { HttpErrorSkip } from '../interceptors/http-error/http-error-skip.model';
import {
  SecurityApiTokenModel,
  SecurityApiToken,
  SecurityApiTokenCache,
} from '@security/models/security-api-token.model';
import { encodeBase64 } from '@shared/utils/base64';
import { MSAL_ACCESS_TOKEN_BUFFER_MS } from '@workers/keep-alive/keep-alive.config';
import {
  InteractionRequiredAuthError,
  SsoSilentRequest,
  AuthenticationResult,
} from '@azure/msal-browser';
import { isInteractionRequiredError } from '@msal/msal.config';
import { MSAL_LOGIN_SCOPES } from '@msal/msal.config';
import { MsalAuthenticationService } from '@security/msal-authentication.service';
import { MSAL_DEVICE_ID_TTL_MS } from '@security/models/msal-authentication.model';

@Injectable({
  providedIn: 'root',
})
export class MsalTokenProviderService {
  private tokenCache = new Map<string, SecurityApiTokenCache>();
  private tokenLoading = new Map<string, Observable<SecurityApiTokenCache>>();
  private isRefreshingExpiredTokens = false;
  private challengeRedirectInProgress = false;
  private readonly http = inject(HttpClient);
  private readonly msalAuthService = inject(MsalAuthenticationService);
  private readonly securityApiUrl =
    inject<ConfigSettings>(APP_CONFIG).securityApiUrl;
  private readonly msalAuthority =
    inject<ConfigSettings>(APP_CONFIG).msalConfig.authority;
  private deviceIdTokenCache: {
    value: string | null;
    checkedAt: number;
  } | null = null;
  private deviceIdTokenLoading: Observable<string | null> | null = null;
  private silentSsoIdTokenLoading: Observable<string | null> | null = null;

  /**
   * Hydrate the MSAL "logical" account from a backend-provided id_token so the
   * rest of the app (auth guard, UI, keep-alive) can identify the signed-in
   * user even when there is no real MSAL session in the browser (passkey or
   * QR/device login). Tokens are not injected into MSAL's cache — they keep
   * coming from the backend through this service.
   */
  public ensureMsalSessionFromIdToken(
    msalIdToken: string | null
  ): Observable<void> {
    if (!msalIdToken) return of(void 0);
    this.msalAuthService.hydrateSyntheticAccountFromIdToken(msalIdToken);
    return of(void 0);
  }

  public getMsalAccessToken(
    scopesList: string[],
    forceRefreshToken: boolean = false,
    tenantId: string = '',
    useHomeTenant: boolean = false
  ): Observable<SecurityApiToken> {
    const useTenant = useHomeTenant
      ? (this.msalAuthService.getHomeTenant()?.tenantId ?? '')
      : tenantId;

    const scopes = scopesList ? scopesList.join(' ') : '';

    return this.findInCache(scopes, useTenant, forceRefreshToken).pipe(
      mergeMap(cacheToken =>
        cacheToken
          ? of(cacheToken)
          : from(this.getIdToken(forceRefreshToken)).pipe(
              mergeMap(idToken =>
                idToken
                  ? of(idToken)
                  : this.trySilentSsoAndGetIdToken(forceRefreshToken)
              ),
              mergeMap(idToken => {
                return this.getMsalTokenFromSecurityApi(
                  idToken,
                  scopes,
                  useTenant
                );
              })
            )
      ),
      map(cachedToken => {
        const { refreshedTokenSignal: _refreshTokenSignal, ...accessToken } =
          cachedToken;
        return accessToken;
      })
    );
  }

  private getIdToken(forceRefreshToken: boolean): Observable<string | null> {
    if (isInFlutterWrapper()) {
      return from(getIdTokenFromFlutterWebViewEvent());
    }

    return this.msalAuthService
      .authenticateFromWebBrowser({
        forceRefresh: forceRefreshToken,
        allowInteraction: false,
      })
      .pipe(
        switchMap(authResult => {
          if (authResult?.idToken) {
            return of(authResult.idToken);
          }

          if (this.msalAuthService.hasRealMsalAccount()) {
            return of(null);
          }

          return this.getPreferredDeviceSessionIdToken(forceRefreshToken);
        })
      );
  }

  private getPreferredDeviceSessionIdToken(
    forceRefreshToken: boolean
  ): Observable<string | null> {
    const cachedToken = this.getCachedDeviceIdToken();

    if (cachedToken) {
      return of(cachedToken);
    }

    return this.getDeviceSessionIdTokenWithStatus().pipe(
      switchMap(({ token: deviceIdToken, unauthorized }) => {
        this.deviceIdTokenCache = {
          value: deviceIdToken,
          checkedAt: Date.now(),
        };

        if (deviceIdToken) {
          return this.ensureMsalSessionFromIdToken(deviceIdToken).pipe(
            map(() => deviceIdToken)
          );
        }

        if (unauthorized) {
          return this.trySilentSsoAndGetIdToken(forceRefreshToken);
        }

        return of(null);
      })
    );
  }

  private getCachedDeviceIdToken(): string | null {
    if (!this.deviceIdTokenCache?.value) {
      return null;
    }

    const isValid =
      Date.now() - this.deviceIdTokenCache.checkedAt < MSAL_DEVICE_ID_TTL_MS;

    return isValid ? this.deviceIdTokenCache.value : null;
  }

  private trySilentSsoAndGetIdToken(
    forceRefreshToken: boolean
  ): Observable<string | null> {
    if (this.silentSsoIdTokenLoading) {
      return this.silentSsoIdTokenLoading;
    }

    const request: SsoSilentRequest = {
      scopes: MSAL_LOGIN_SCOPES,
      authority: this.msalAuthority,
    };

    const silentSso$ = this.msalAuthService.authenticateSilently(request).pipe(
      catchError(error => {
        console.debug('MSAL SSO silent authentication failed', error);
        return of(null);
      }),
      switchMap((authResult: AuthenticationResult | null) => {
        if (authResult && authResult?.idToken) {
          return of(authResult.idToken);
        }

        return this.msalAuthService
          .authenticateFromWebBrowser({
            forceRefresh: forceRefreshToken,
            allowInteraction: false,
          })
          .pipe(map(authResult => authResult?.idToken ?? null));
      }),
      shareReplay(1),
      finalize(() => {
        this.silentSsoIdTokenLoading = null;
      })
    );

    this.silentSsoIdTokenLoading = silentSso$;

    return silentSso$;
  }

  private fetchTokenFromSecurityApi(
    idToken: string | null | undefined,
    scopes: string,
    tenantId: string
  ): Observable<SecurityApiTokenModel> {
    const resolvedIdToken$ = idToken
      ? of(idToken)
      : from(this.getIdToken(false));

    return resolvedIdToken$.pipe(
      mergeMap(token => {
        if (!token) {
          return throwError(
            () => new Error('No MSAL session available to get token')
          );
        }

        const bodyObject = {
          scopes: encodeBase64(scopes),
          tenantId,
        };

        const headers = new HttpHeaders({
          Authorization: `Bearer ${encodeBase64(token)}`,
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [HttpStatusCode.Unauthorized],
          }).parseToString(),
        });

        return this.http
          .post<SecurityApiTokenModel>(
            `${this.securityApiUrl}/auth/azure-token`,
            bodyObject,
            {
              headers,
            }
          )
          .pipe(catchError(err => this.handleInteractionRequired(err, scopes)));
      })
    );
  }

  private buildTokenCache(
    token: SecurityApiTokenModel,
    scopes: string,
    tenantId: string,
    existingSignal?: SecurityApiTokenCache['refreshedTokenSignal']
  ): SecurityApiTokenCache {
    const refreshedTokenSignal =
      existingSignal ?? signal<SecurityApiToken | null>(null);
    return {
      accessToken: token.accessToken,
      expiresOn: new Date(token.expiresOn),
      scopes,
      tenantId,
      refreshedToken: refreshedTokenSignal.asReadonly(),
      refreshedTokenSignal,
    };
  }

  private getMsalTokenFromSecurityApi(
    msalIdToken: string,
    scopes: string,
    tenantId: string
  ): Observable<SecurityApiTokenCache> {
    const newTokenRequest = this.fetchTokenFromSecurityApi(
      msalIdToken,
      scopes,
      tenantId
    ).pipe(
      map(token => {
        const msalAccessToken = this.buildTokenCache(token, scopes, tenantId);
        this.saveToken(scopes, tenantId, msalAccessToken);
        return msalAccessToken;
      }),
      shareReplay(1)
    );

    return this.prepareToken(scopes, tenantId, newTokenRequest);
  }

  public handleInteractionRequired(err, scopes: string) {
    console.debug('Error acquiring OBO with security-api', err);
    if (err.status === HttpStatusCode.Unauthorized) {
      const details = err?.error?.details || err?.error?.error?.details;
      const authError: Partial<InteractionRequiredAuthError> | null =
        details?.authError;
      if (
        details &&
        authError &&
        isInteractionRequiredError(
          authError.errorCode,
          authError.errorMessage,
          authError.subError
        )
      ) {
        // Prevent multiple redirect flows requests that causes multiple page redirects
        if (this.challengeRedirectInProgress) {
          return EMPTY;
        }
        this.challengeRedirectInProgress = true;
        const { tenantId, claims } = details;
        return this.handleMfaChallenge(claims, tenantId, scopes).pipe(
          finalize(() => {
            this.challengeRedirectInProgress = false;
          }),
          ignoreElements()
        );
      }
    }

    return throwError(() => err);
  }

  private handleMfaChallenge(claims: string, tenantId: string, scopes: string) {
    console.debug(
      'handling InteractionRequiredAuthError. Acquiring token redirect with claims'
    );

    return this.msalAuthService.handleMfaChallenge(claims, tenantId, scopes);
  }

  private getCacheKey(scopes: string, tenantId: string | null): string {
    return tenantId ? `${tenantId}-${scopes}` : scopes;
  }

  private prepareToken(
    scopes: string,
    tenantId: string,
    current: Observable<SecurityApiTokenCache>
  ): Observable<SecurityApiTokenCache> {
    const cacheKey = this.getCacheKey(scopes, tenantId);

    // Return loading HTTP request if available
    const loading = this.tokenLoading.get(cacheKey);
    if (loading) {
      return loading;
    }

    const sharedCurrent = current.pipe(
      shareReplay(1),
      finalize(() => {
        if (this.tokenLoading.get(cacheKey) === sharedCurrent) {
          this.tokenLoading.delete(cacheKey);
        }
      })
    );

    this.tokenLoading.set(cacheKey, sharedCurrent);

    return sharedCurrent;
  }

  private deleteFromCache(cacheKey: string) {
    this.tokenCache.delete(cacheKey);
    this.tokenLoading.delete(cacheKey);
  }

  private saveToken(
    scopes: string,
    tenantId: string,
    msalAccessToken: SecurityApiTokenCache
  ) {
    const cacheKey = this.getCacheKey(scopes, tenantId);
    // Delete all loading requests and previous tokens before saving a new token
    this.deleteFromCache(cacheKey);
    this.tokenCache.set(cacheKey, msalAccessToken);
  }

  private findInCache(
    scopes: string,
    tenantId: string,
    forceRefresh: boolean
  ): Observable<SecurityApiTokenCache | null> {
    const cacheKey = this.getCacheKey(scopes, tenantId);

    if (forceRefresh) {
      this.deleteFromCache(cacheKey);
      return of(null);
    }

    // Return loading HTTP request if available
    const loading = this.tokenLoading.get(cacheKey);
    if (loading) {
      return loading;
    }

    // Return cached token if is not expired
    const cachedToken = this.tokenCache.get(cacheKey);
    if (!cachedToken) {
      return of(null);
    }

    // If token is expired, refresh it while keeping the signal reference
    if (this.tokenIsExpired(cachedToken)) {
      return this.refreshCachedToken(cacheKey, cachedToken);
    }

    return of(cachedToken);
  }

  private refreshCachedToken(
    cacheKey: string,
    cachedToken: SecurityApiTokenCache
  ): Observable<SecurityApiTokenCache> {
    const { scopes, tenantId, refreshedTokenSignal } = cachedToken;

    const refreshRequest = this.getIdToken(false).pipe(
      mergeMap(idToken =>
        idToken ? of(idToken) : this.trySilentSsoAndGetIdToken(false)
      ),
      mergeMap(idToken =>
        this.fetchTokenFromSecurityApi(idToken, scopes, tenantId).pipe(
          map(newToken => {
            const updatedToken = this.buildTokenCache(
              newToken,
              scopes,
              tenantId,
              refreshedTokenSignal
            );

            this.tokenCache.set(cacheKey, updatedToken);
            this.tokenLoading.delete(cacheKey);

            refreshedTokenSignal.set({
              accessToken: newToken.accessToken,
              expiresOn: new Date(newToken.expiresOn),
              scopes,
              tenantId,
              refreshedToken: updatedToken.refreshedToken,
            });

            return updatedToken;
          })
        )
      ),
      shareReplay(1),
      finalize(() => {
        if (this.tokenLoading.get(cacheKey) === refreshRequest) {
          this.tokenLoading.delete(cacheKey);
        }
      })
    );

    this.tokenLoading.set(cacheKey, refreshRequest);
    return refreshRequest;
  }

  private tokenIsExpired(msalAccessToken: SecurityApiTokenCache): boolean {
    // MSAL only lets you refresh the token when it is near to expire.
    // We consider an expired token if it expires in less than 5 min
    if (!msalAccessToken.expiresOn) {
      return true;
    }
    const expiresAt = new Date(msalAccessToken.expiresOn).getTime();
    const threshold = Date.now() + MSAL_ACCESS_TOKEN_BUFFER_MS;
    return expiresAt <= threshold;
  }

  public refreshExpiredTokens(): Observable<null> {
    if (this.isRefreshingExpiredTokens) {
      return of(null);
    }

    const expiredTokens = Array.from(this.tokenCache.entries()).filter(
      ([_, token]) => this.tokenIsExpired(token)
    );

    if (expiredTokens.length === 0) {
      return of(null);
    }

    this.isRefreshingExpiredTokens = true;

    return this.getIdToken(false).pipe(
      catchError(() => of(null)),
      mergeMap(idToken =>
        idToken ? of(idToken) : this.trySilentSsoAndGetIdToken(false)
      ),
      mergeMap(msalIdToken => {
        if (!msalIdToken) {
          return of(null);
        }

        return this.ensureMsalSessionFromIdToken(msalIdToken).pipe(
          switchMap(() =>
            zip(
              expiredTokens.map(([cacheKey, cachedToken]) =>
                this.refreshTokenAndUpdateSignal(
                  cacheKey,
                  cachedToken,
                  msalIdToken
                )
              )
            ).pipe(ignoreElements())
          )
        );
      }),
      finalize(() => {
        this.isRefreshingExpiredTokens = false;
      })
    );
  }

  private refreshTokenAndUpdateSignal(
    cacheKey: string,
    cachedToken: SecurityApiTokenCache,
    idToken: string
  ): Observable<void> {
    const { scopes, tenantId, refreshedTokenSignal } = cachedToken;
    return this.fetchTokenFromSecurityApi(idToken, scopes, tenantId).pipe(
      map(newToken => {
        const updatedToken = this.buildTokenCache(newToken, scopes, tenantId);
        this.tokenCache.set(cacheKey, updatedToken);
        this.tokenLoading.delete(cacheKey);

        refreshedTokenSignal.set({
          accessToken: newToken.accessToken,
          expiresOn: new Date(newToken.expiresOn),
          scopes,
          tenantId,
          refreshedToken: updatedToken.refreshedToken,
        });
      }),
      catchError(error => {
        console.error(
          `Failed to refresh MSAL access token for cache key ${cacheKey}:`,
          error
        );
        this.deleteFromCache(cacheKey);
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieve an ID token from the backend device session.
   * Returns `null` when no active device session exists.
   */
  public getIdTokenFromDeviceSession(): Observable<string | null> {
    return this.getDeviceSessionIdTokenWithStatus().pipe(
      map(({ token }) => token)
    );
  }

  private getDeviceSessionIdTokenWithStatus(): Observable<{
    token: string | null;
    unauthorized: boolean;
  }> {
    if (this.deviceIdTokenLoading) {
      return this.deviceIdTokenLoading.pipe(
        map(token => ({ token, unauthorized: false }))
      );
    }

    const deviceRequest$ = this.http
      .get<{
        idToken: string;
      }>(`${this.securityApiUrl}${SECURITY_API_URL.deviceToken}`, {
        withCredentials: true,
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [
              HttpStatusCode.Unauthorized,
              HttpStatusCode.Forbidden,
              HttpStatusCode.NotFound,
              HttpStatusCode.InternalServerError,
            ],
          }).parseToString(),
        },
      })
      .pipe(
        map(token => token.idToken),
        shareReplay(1),
        finalize(() => {
          this.deviceIdTokenLoading = null;
        })
      );

    this.deviceIdTokenLoading = deviceRequest$;

    return deviceRequest$.pipe(
      switchMap(token =>
        token
          ? this.ensureMsalSessionFromIdToken(token).pipe(
              map(() => ({ token, unauthorized: false }))
            )
          : of({ token, unauthorized: false })
      ),
      catchError((error: any) => {
        this.deviceIdTokenLoading = null;
        return of({
          token: null,
          unauthorized: error?.status === HttpStatusCode.Unauthorized,
        });
      })
    );
  }
}
